import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { CheckCircle2, XCircle, ArrowRight, Trophy, ClipboardList, BookOpen, ListChecks } from "lucide-react";

const MYTH_KEYS = [1, 2, 3, 4];

export default function Home() {
  const { t, lang } = useLanguage();
  const [stats, setStats] = useState(null);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get(`/blog?lang=${lang}`).then((r) => setPosts(r.data.slice(0, 3))).catch(() => {});
  }, [lang]);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 paper-grid opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-20 pb-16 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <motion.span
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-3 py-1"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-teal-500" /> {t("home.badge")}
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
              className="mt-5 font-serif font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 leading-[1.05]"
            >
              {t("home.h1a")}<span className="text-teal-600">NLP</span>{t("home.h1b")}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
              className="mt-6 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed"
            >
              {t("home.intro")}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }}
              className="mt-8 flex flex-wrap gap-3"
            >
              <Link to="/test/knowledge">
                <Button data-testid="start-knowledge-test-btn" className="bg-slate-900 hover:bg-slate-800 text-white rounded-full h-12 px-7 text-base">
                  {t("home.ctaKnowledge")} <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
              <Link to="/checklist/fake-nlp">
                <Button data-testid="start-checklist-btn" variant="outline" className="rounded-full h-12 px-7 text-base border-slate-300">
                  {t("home.ctaScore")}
                </Button>
              </Link>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }}
            className="md:col-span-5"
          >
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
              <div className="font-mono text-xs uppercase tracking-widest text-slate-400 mb-4">{t("home.liveIndex")}</div>
              <div className="grid grid-cols-2 gap-4">
                <Stat label={t("home.stat.knowledge")} value={stats?.knowledge_submissions} />
                <Stat label={t("home.stat.trainers")} value={stats?.trainers_rated} />
                <Stat label={t("home.stat.checklist")} value={stats?.checklist_submissions} />
                <Stat label={t("home.stat.members")} value={stats?.members} />
              </div>
              <div className="mt-6 pt-5 border-t border-slate-100 space-y-3">
                <QuickLink to="/leaderboards" icon={Trophy} title={t("home.quick.leaderboards")} desc={t("home.quick.leaderboardsDesc")} />
                <QuickLink to="/blog" icon={BookOpen} title={t("home.quick.blog")} desc={t("home.quick.blogDesc")} />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Myth vs Truth */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20">
        <div className="max-w-2xl">
          <div className="font-mono text-xs uppercase tracking-widest text-slate-400 mb-3">{t("home.matrix.kicker")}</div>
          <h2 className="font-serif font-semibold text-3xl lg:text-4xl tracking-tight text-slate-900">{t("home.matrix.title")}</h2>
        </div>
        <div className="mt-10 grid md:grid-cols-2 gap-5">
          {MYTH_KEYS.map((m, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="bg-white border border-slate-200 rounded-xl p-6 hover:-translate-y-0.5 hover:shadow-md transition-all"
            >
              <div className="flex items-start gap-3 mb-4">
                <XCircle className="text-rose-500 shrink-0 mt-0.5" size={20} />
                <p className="text-slate-500 line-through decoration-rose-300">{t(`myth.${m}.myth`)}</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="text-teal-600 shrink-0 mt-0.5" size={20} />
                <p className="text-slate-800 font-medium">{t(`myth.${m}.truth`)}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Dual launcher */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 grid md:grid-cols-2 gap-6">
          <Launcher
            to="/test/knowledge" icon={ListChecks} tag={t("home.launch.knowledgeTag")}
            title={t("home.launch.knowledgeTitle")}
            desc={t("home.launch.knowledgeDesc")}
            cta={t("home.launch.knowledgeCta")} testid="launch-knowledge"
          />
          <Launcher
            to="/checklist/fake-nlp" icon={ClipboardList} tag={t("home.launch.checklistTag")}
            title={t("home.launch.checklistTitle")}
            desc={t("home.launch.checklistDesc")}
            cta={t("home.launch.checklistCta")} testid="launch-checklist"
          />
        </div>
      </section>

      {/* Blog teaser */}
      {posts.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-10 py-20">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="font-mono text-xs uppercase tracking-widest text-slate-400 mb-3">{t("home.blog.kicker")}</div>
              <h2 className="font-serif font-semibold text-3xl lg:text-4xl tracking-tight text-slate-900">{t("home.blog.title")}</h2>
            </div>
            <Link to="/blog" className="hidden sm:flex items-center gap-1.5 text-teal-700 font-medium hover:gap-2.5 transition-all">
              {t("home.blog.all")} <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {posts.map((p) => (
              <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`home-post-${p.slug}`} className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:-translate-y-0.5 hover:shadow-md transition-all">
                <div className="aspect-[16/9] overflow-hidden bg-slate-100">
                  {p.cover_image ? (
                    <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : <div className="w-full h-full bg-gradient-to-br from-slate-100 to-slate-200" />}
                </div>
                <div className="p-5">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-teal-700">{p.category}</span>
                  <h3 className="mt-2 font-serif font-bold text-lg text-slate-900 leading-snug group-hover:text-teal-700 transition-colors">{p.title}</h3>
                  <p className="mt-2 text-sm text-slate-500 line-clamp-2">{p.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
      <div className="font-serif font-bold text-3xl text-slate-900">{value ?? "—"}</div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mt-1">{label}</div>
    </div>
  );
}

function QuickLink({ to, icon: Icon, title, desc }) {
  return (
    <Link to={to} className="flex items-center gap-3 group">
      <span className="grid place-items-center w-10 h-10 rounded-lg bg-teal-50 text-teal-700 border border-teal-100 group-hover:bg-teal-100 transition-colors">
        <Icon size={18} />
      </span>
      <span>
        <span className="block text-sm font-semibold text-slate-900">{title}</span>
        <span className="block text-xs text-slate-500">{desc}</span>
      </span>
    </Link>
  );
}

function Launcher({ to, icon: Icon, tag, title, desc, cta, testid }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      className="bg-white/5 border border-white/10 rounded-2xl p-8 flex flex-col"
    >
      <div className="flex items-center justify-between">
        <span className="grid place-items-center w-12 h-12 rounded-xl bg-teal-500/15 text-teal-300 border border-teal-400/20"><Icon size={22} /></span>
        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400 bg-white/5 border border-white/10 rounded-full px-3 py-1">{tag}</span>
      </div>
      <h3 className="mt-6 font-serif font-bold text-2xl">{title}</h3>
      <p className="mt-3 text-slate-300 leading-relaxed flex-1">{desc}</p>
      <Link to={to} className="mt-6">
        <Button data-testid={testid} className="bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold rounded-full h-11 px-6">
          {cta} <ArrowRight size={17} className="ml-2" />
        </Button>
      </Link>
    </motion.div>
  );
}
